import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Autocomplete,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { Event, Account } from '../types';
import { api } from '../services/api';
import { useConfigStore } from '../stores/useConfigStore';
import dayjs, { Dayjs } from 'dayjs';

const PAGE_SIZE = 50;
/** A filtered page can legitimately come back empty with a cursor; keep going this many times. */
const MAX_EMPTY_PAGE_FOLLOWS = 5;

interface EventFilters {
  accountId?: string;
  eventType?: string;
  startDate: string;
  endDate: string;
}

/**
 * Events Page
 * View and search events with filters + infinite scroll.
 */
export const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [nextToken, setNextToken] = useState<string | undefined>();

  // Filters - Default to last 7 days (end of today, inclusive)
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [selectedEventType, setSelectedEventType] = useState<string>('all');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(7, 'day').startOf('day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('day'));

  // Infinite scroll + stale-response guards
  const observerTarget = useRef<HTMLDivElement>(null);
  const requestSeq = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  // Load accounts for the filter dropdown (all pages, capped)
  useEffect(() => {
    let cancelled = false;
    api.listAllAccounts()
      .then((all) => { if (!cancelled) setAccounts(all); })
      .catch((err) => console.error('Error loading accounts:', err));
    return () => { cancelled = true; };
  }, []);

  const filters = useMemo<EventFilters | null>(() => {
    if (!startDate || !endDate) return null;
    return {
      accountId: selectedAccount?.accountId,
      eventType: selectedEventType && selectedEventType !== 'all' ? selectedEventType : undefined,
      // Whole days in UTC: DatePicker gives local midnight; normalise so a
      // picked End Date includes that entire day.
      startDate: startDate.startOf('day').toISOString(),
      endDate: endDate.endOf('day').toISOString(),
    };
  }, [selectedAccount, selectedEventType, startDate, endDate]);

  /**
   * Fetch one logical page: follows the cursor while pages come back empty
   * (server-side FilterExpression) so the UI never dead-ends on "no events"
   * with more data behind the cursor.
   */
  const fetchPage = useCallback(async (f: EventFilters, token: string | undefined, signal: AbortSignal) => {
    let collected: Event[] = [];
    let cursor = token;
    for (let i = 0; i <= MAX_EMPTY_PAGE_FOLLOWS; i++) {
      const result = await api.listEvents({ ...f, limit: PAGE_SIZE, nextToken: cursor }, { signal });
      collected = collected.concat(result.events);
      cursor = result.nextToken;
      if (collected.length > 0 || !cursor) break;
    }
    return { events: collected, nextToken: cursor };
  }, []);

  // Load events whenever filters change (aborting any in-flight request)
  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++requestSeq.current;

    setEvents([]);
    setNextToken(undefined);
    setExpandedRows(new Set());
    setError(null);

    if (!filters) {
      setError('Please select a date range to view events');
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchPage(filters, undefined, controller.signal)
      .then((result) => {
        if (seq !== requestSeq.current) return; // stale
        setEvents(result.events);
        setNextToken(result.nextToken);
      })
      .catch((err: any) => {
        if (controller.signal.aborted || seq !== requestSeq.current) return;
        console.error('Error loading events:', err);
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load events');
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });

    return () => controller.abort();
  }, [filters, fetchPage]);

  // Load more events for infinite scroll
  const loadMoreEvents = useCallback(async () => {
    if (loadingMore || !nextToken || !filters) return;
    const seq = requestSeq.current;
    const controller = abortRef.current ?? new AbortController();

    setLoadingMore(true);
    try {
      const result = await fetchPage(filters, nextToken, controller.signal);
      if (seq !== requestSeq.current) return; // filters changed meanwhile
      setEvents((prev) => {
        const seen = new Set(prev.map((e) => e.eventId));
        return [...prev, ...result.events.filter((e) => !seen.has(e.eventId))];
      });
      setNextToken(result.nextToken);
    } catch (err: any) {
      if (!controller.signal.aborted) console.error('Error loading more events:', err);
    } finally {
      if (seq === requestSeq.current) setLoadingMore(false);
    }
  }, [loadingMore, nextToken, filters, fetchPage]);

  // Infinite scroll observer
  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && nextToken && !loadingMore && !loading) {
          loadMoreEvents();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(target);
    return () => observer.unobserve(target);
  }, [loadMoreEvents, nextToken, loadingMore, loading]);

  const toggleRow = (eventId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) newSet.delete(eventId);
      else newSet.add(eventId);
      return newSet;
    });
  };

  const accountsById = useMemo(() => {
    const map = new Map<string, Account>();
    accounts.forEach((a) => map.set(a.accountId, a));
    return map;
  }, [accounts]);

  const getAccountName = (accountId: string) => {
    const account = accountsById.get(accountId);
    return account?.metadata?.companyName || account?.metadata?.ownerEmail || accountId;
  };

  // Get event types from config store (auto-tracked from all events)
  const configEventTypes = useConfigStore((state) => state.config?.eventTypes);
  const availableEventTypes = useMemo(() => {
    if (!configEventTypes) return [];
    return Object.keys(configEventTypes).sort();
  }, [configEventTypes]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Events
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          View and search event logs (showing last 7 days by default, scroll to load more)
        </Typography>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Filters
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Autocomplete
              value={selectedAccount}
              onChange={(_, newValue) => setSelectedAccount(newValue)}
              options={accounts}
              getOptionLabel={(option) =>
                option.metadata?.companyName || option.metadata?.ownerEmail || option.accountId
              }
              isOptionEqualToValue={(option, value) => option.accountId === value.accountId}
              renderInput={(params) => (
                <TextField {...params} label="Account" placeholder="All Accounts" size="small" />
              )}
              sx={{ flex: 1, minWidth: 200 }}
            />

            <TextField
              select
              label="Event Type"
              value={selectedEventType}
              onChange={(e) => setSelectedEventType(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
              size="small"
            >
              <MenuItem value="all">All Event Types</MenuItem>
              {availableEventTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>

            <DatePicker
              label="Start Date"
              value={startDate}
              maxDate={endDate ?? undefined}
              onChange={(newValue) => setStartDate(newValue ? newValue.startOf('day') : null)}
              slotProps={{ textField: { size: 'small', sx: { flex: 1, minWidth: 200 } } }}
            />

            <DatePicker
              label="End Date"
              value={endDate}
              minDate={startDate ?? undefined}
              onChange={(newValue) => setEndDate(newValue ? newValue.endOf('day') : null)}
              slotProps={{ textField: { size: 'small', sx: { flex: 1, minWidth: 200 } } }}
            />
          </Stack>
        </Paper>

        {/* Events Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="info">{error}</Alert>
        ) : events.length === 0 ? (
          <Alert severity="info">No events found for the selected filters</Alert>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell width={50} />
                  <TableCell>
                    <Typography variant="caption" fontWeight={600} textTransform="uppercase">
                      Timestamp
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontWeight={600} textTransform="uppercase">
                      Event Type
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontWeight={600} textTransform="uppercase">
                      Account
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event) => (
                  <React.Fragment key={event.eventId}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleRow(event.eventId)}>
                      <TableCell>
                        <IconButton size="small">
                          {expandedRows.has(event.eventId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{new Date(event.timestamp).toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={event.eventType}
                          size="small"
                          sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600, px: 2, py: 2 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{getAccountName(event.accountId)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {event.accountId}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                        <Collapse in={expandedRows.has(event.eventId)} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 3, bgcolor: 'background.default' }}>
                            <Typography variant="subtitle2" fontWeight={600} mb={1}>
                              Event Details
                            </Typography>
                            <Stack spacing={1}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Event ID
                                </Typography>
                                <Typography variant="body2" fontFamily="monospace">
                                  {event.eventId}
                                </Typography>
                              </Box>
                              {event.source && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Source
                                  </Typography>
                                  <Typography variant="body2">{event.source}</Typography>
                                </Box>
                              )}
                              {event.userId && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    User ID
                                  </Typography>
                                  <Typography variant="body2">{event.userId}</Typography>
                                </Box>
                              )}
                              {event.data && Object.keys(event.data).length > 0 && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" mb={0.5}>
                                    Data
                                  </Typography>
                                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                    <Stack spacing={1}>
                                      {Object.entries(event.data).map(([key, value]) => (
                                        <Box key={key}>
                                          <Typography variant="caption" fontWeight={600} color="text.secondary">
                                            {key}:
                                          </Typography>{' '}
                                          <Typography variant="body2" component="span" fontFamily="monospace">
                                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Stack>
                                  </Paper>
                                </Box>
                              )}
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Infinite scroll trigger: always mounted so loading can continue
            even when a page came back empty. */}
        {!loading && !error && (
          <Box ref={observerTarget} sx={{ py: 2, textAlign: 'center' }}>
            {loadingMore && <CircularProgress size={24} />}
            {!loadingMore && !nextToken && events.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                No more events
              </Typography>
            )}
          </Box>
        )}

        {events.length > 0 && (
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary">
              Showing {events.length} event{events.length !== 1 ? 's' : ''} {nextToken && '(scroll for more)'}
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};
